import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { logout, type Me } from "@wilco/api";
import { applyTheme, Button, IconLogout, IconMoon, IconSun, IconUser, readTheme, useToast, type ThemeChoice } from "@wilco/ui";
import { DropdownMenu } from "radix-ui";
import { useState } from "react";
import { t } from "../i18n.ts";

/** Меню пользователя: кто вошёл, тема на устройство, выход. */
export function UserMenu({ me }: { me: Me }) {
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const choose = (value: string) => {
    const choice = value as ThemeChoice;
    applyTheme(choice);
    setTheme(choice);
  };

  const signOut = async () => {
    try {
      await logout();
    } catch {
      toast({ title: t("error.server.title"), tone: "danger" });
      return;
    }
    queryClient.clear();
    await navigate({ to: "/login", search: { out: 1 } });
  };

  return (
    // Тему могла сменить палитра: при открытии читаем заново.
    <DropdownMenu.Root onOpenChange={(open) => open && setTheme(readTheme())}>
      <DropdownMenu.Trigger asChild>
        <Button variant="header" size="icon" aria-label={t("shell.user")}>
          <IconUser />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align="end" sideOffset={4} collisionPadding={8}>
          <div className="menu-who">
            <span className="menu-who-name">{me.displayName}</span>
            <span className="menu-who-meta">{me.email}</span>
            <span className="menu-who-meta">{me.tenantName}</span>
          </div>
          <DropdownMenu.Separator className="menu-sep" />
          <DropdownMenu.Label className="menu-label">{t("shell.theme")}</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={theme} onValueChange={choose}>
            <DropdownMenu.RadioItem value="light" className="menu-item" onSelect={(e) => e.preventDefault()}>
              <IconSun />
              {t("shell.theme.light")}
            </DropdownMenu.RadioItem>
            <DropdownMenu.RadioItem value="dark" className="menu-item" onSelect={(e) => e.preventDefault()}>
              <IconMoon />
              {t("shell.theme.dark")}
            </DropdownMenu.RadioItem>
            <DropdownMenu.RadioItem value="system" className="menu-item" onSelect={(e) => e.preventDefault()}>
              <span className="menu-item-spacer" aria-hidden="true" />
              {t("shell.theme.system")}
            </DropdownMenu.RadioItem>
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="menu-sep" />
          <DropdownMenu.Item className="menu-item" onSelect={() => void signOut()}>
            <IconLogout />
            {t("shell.logout")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
